import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";
import {
  ConversationId,
  ConversationThreadId,
  DraftMessageId,
  MessageId,
} from "../ids/definitions";
import {
  StartBulkConversationActionInput,
  StartBulkConversationActionResponse,
} from "../models/shopper-ai/conversation/bulk-conversation-action";
import {
  ConversationListCursor,
  ConversationListItemResponse,
  ConversationResponse,
  CreateDraftConversationInput,
  CreateDraftConversationResponse,
  PublishDraftConversationInput,
  StartConversationInput,
  StartConversationResponse,
  UpdateConversationInput,
} from "../models/shopper-ai/conversation/conversation";
import {
  DraftResponse,
  SaveDraftInput,
  SendMessagePayload,
  ThreadDraftsResponse,
} from "../models/shopper-ai/conversation/draft";
import {
  Message,
  MessageCursor,
} from "../models/shopper-ai/conversation/message";
import {
  ConversationThread,
  ConversationThreadCursor,
} from "../models/shopper-ai/conversation/thread";
import { ConversationTimelineEventResponse } from "../models/shopper-ai/conversation/timeline-event/timeline-event-response";
import {
  CursorPaginatedResponse,
  CursorPaginationParams,
  CursorPaginationWithConversationQueryParams,
} from "../schema/pagination";

export const ConversationGroupBase = HttpApiGroup.make("conversation")
  .add(
    endpoint.merchant
      .post("startConversation", "/conversations/start")
      .setPayload(StartConversationInput)
      .addSuccess(StartConversationResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createDraftConversation", "/conversations/draft")
      .setPayload(CreateDraftConversationInput)
      .addSuccess(CreateDraftConversationResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("publishDraftConversation", "/conversations/:id/publish")
      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(PublishDraftConversationInput)
      .addSuccess(StartConversationResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getFilteredConversations", "/conversations")
      .setUrlParams(
        CursorPaginationWithConversationQueryParams(ConversationListCursor),
      )
      .addSuccess(
        CursorPaginatedResponse(
          ConversationListItemResponse,
          ConversationListCursor,
        ),
      )
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getConversation", "/conversations/:id")

      .setPath(Schema.Struct({ id: ConversationId }))
      .addSuccess(ConversationListItemResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getConversationThreads", "/conversations/:id/threads")

      .setPath(Schema.Struct({ id: ConversationId }))
      .setUrlParams(CursorPaginationParams(ConversationThreadCursor))
      .addSuccess(
        CursorPaginatedResponse(ConversationThread, ConversationThreadCursor),
      )
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getConversationMessages", "/conversations/:id/messages")

      .setPath(Schema.Struct({ id: ConversationId }))
      .setUrlParams(CursorPaginationParams(MessageCursor))
      .addSuccess(CursorPaginatedResponse(Message, MessageCursor))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateConversation", "/conversations/:id")

      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(UpdateConversationInput)
      .addSuccess(ConversationResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("startBulkConversationAction", "/conversations/bulk")

      .setPayload(StartBulkConversationActionInput)
      .addSuccess(StartBulkConversationActionResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("saveDraft", "/conversations/:id/drafts")

      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(SaveDraftInput)
      .addSuccess(DraftResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getDraft", "/conversations/:id/drafts")

      .setPath(Schema.Struct({ id: ConversationId }))
      .setUrlParams(
        Schema.Struct({
          threadId: ConversationThreadId,
          inReplyToMessageId: Schema.optional(MessageId),
        }),
      )
      .addSuccess(Schema.NullOr(DraftResponse))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("getConversationDrafts", "/conversations/:id/drafts/all")

      .setPath(Schema.Struct({ id: ConversationId }))
      .addSuccess(ThreadDraftsResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteDraft", "/conversations/:id/drafts/:draftId")

      .setPath(Schema.Struct({ id: ConversationId, draftId: DraftMessageId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("sendMessage", "/conversations/:id/messages")

      .setPath(Schema.Struct({ id: ConversationId }))
      .setPayload(SendMessagePayload)
      .addSuccess(Schema.Struct({ workflowId: Schema.String }))
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getConversationTimelineEvents",
        "/conversations/:id/timeline-events",
      )

      .setPath(Schema.Struct({ id: ConversationId }))
      .addSuccess(
        Schema.Struct({
          items: Schema.Array(ConversationTimelineEventResponse),
        }),
      )
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  );
